import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listReports, ReportSummary } from "../api/reports";
import LoadingSpinner from "../components/LoadingSpinner";

const recColor: Record<string, string> = {
  "proceed": "bg-white text-maestro-900 border-maestro-900",
  "proceed with caution": "bg-white text-maestro-700 border-maestro-300",
  "do not proceed": "bg-maestro-900 text-white border-maestro-900",
  "pending": "bg-maestro-50 text-maestro-400 border-maestro-200",
  "running": "bg-maestro-50 text-maestro-400 border-maestro-200",
  "failed": "bg-white text-red-700 border-red-300",
};

function displayBadge(r: { status?: string; recommendation?: string }): string {
  const s = r.status;
  if (s === "pending" || s === "running" || s === "failed") return s;
  return r.recommendation || "—";
}

export default function Reports() {
  const [reports, setReports] = useState<ReportSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listReports()
      .then(setReports)
      .catch(() => setError("Could not load reports"));
  }, []);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!reports) return <LoadingSpinner />;

  if (reports.length === 0) {
    return (
      <div>
        <h1 className="font-display text-2xl sm:text-3xl text-maestro-900 mb-4">
          Clearance reports
        </h1>
        <p className="text-maestro-700 text-sm">
          No reports yet.{" "}
          <Link to="/randes-search" className="text-maestro-900 underline hover:no-underline">
            Run your first clearance
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl text-maestro-900 mb-6">
        Clearance reports
      </h1>

      {/* Mobile: stacked cards */}
      <ul className="sm:hidden space-y-3">
        {reports.map((r) => (
          <li key={r.report_id} className="bg-white border border-maestro-200 p-4">
            <Link to={`/reports/${r.report_id}`} className="block">
              <div className="flex items-start justify-between gap-2">
                <span className="font-display text-maestro-900 break-words">{r.brand_name}</span>
                <span
                  className={`text-[0.65rem] uppercase tracking-wider px-2 py-0.5 border whitespace-nowrap ${recColor[displayBadge(r)] || "border-maestro-200 text-maestro-400"}`}
                >
                  {displayBadge(r)}
                </span>
              </div>
              <div className="mt-2 text-xs text-maestro-400 flex flex-wrap gap-x-3 gap-y-1">
                <span>Class {r.nice_classes.join(", ")}</span>
                <span>{r.conflicts_count} conflict{r.conflicts_count === 1 ? "" : "s"}</span>
                <span className="ml-auto">{r.created_at.slice(0, 10)}</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {/* Desktop: table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full bg-white border border-maestro-200">
          <thead className="bg-maestro-50 text-left">
            <tr>
              <th className="px-4 py-3 font-display text-[0.7rem] uppercase tracking-[0.1em] text-maestro-900 border-b border-maestro-200">Brand</th>
              <th className="px-4 py-3 font-display text-[0.7rem] uppercase tracking-[0.1em] text-maestro-900 border-b border-maestro-200">Nice</th>
              <th className="px-4 py-3 font-display text-[0.7rem] uppercase tracking-[0.1em] text-maestro-900 border-b border-maestro-200">Conflicts</th>
              <th className="px-4 py-3 font-display text-[0.7rem] uppercase tracking-[0.1em] text-maestro-900 border-b border-maestro-200">Date</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.report_id} className="border-t border-maestro-200 hover:bg-maestro-50">
                <td className="px-4 py-2 text-sm">
                  <Link to={`/reports/${r.report_id}`} className="text-maestro-900 hover:underline">
                    {r.brand_name}
                  </Link>
                </td>
                <td className="px-4 py-2 text-sm text-maestro-700">{r.nice_classes.join(", ")}</td>
                <td className="px-4 py-2 text-sm text-maestro-700">{r.conflicts_count}</td>
                <td className="px-4 py-2 text-sm text-maestro-400">{r.created_at.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
